import 'twin.macro';

import { DateTime } from '@cheqroom/date-time';
import { Calendar, Deprecation, DotsHorizontal, Email, LinkExternal, Number, Phone, User } from '@cheqroom/icons';
import { Stack } from '@cheqroom/ui';
import { FC, ReactNode, useCallback, useMemo } from 'react';

import { SystemFieldType, VirtualFieldType } from '../../../../../../hooks/item-fields/types';
import { useSettings } from '../../../../../../hooks/settings/use-settings.hook';
import { DateFormatter, NumberFormatter } from '../../../../../../util';
import { Info } from '../../../../../Info/Info';
import { FieldData, renderField } from '../../../../../Info/ItemInfo/components/render-field';
import type { InlineField } from '../../../../types';

type Props = {
	fields: InlineField[];
	isLoading?: boolean;
	wrap?: boolean;
	customInlineFields?: ReactNode;
};

const InlineFields: FC<Props> = ({ fields, isLoading = false, wrap = false, customInlineFields, ...rest }) => {
	const { currency } = useSettings();

	const getIcon = useCallback((field: InlineField) => {
		if (field.icon) return field.icon;

		if (field.id?.startsWith('fields.')) {
			switch (field.kind) {
				case 'customer':
					return User;
			}

			switch (field.type) {
				case 'date':
					return Calendar;
				case 'url':
					return LinkExternal;
				case 'email':
					return Email;
				case 'phone':
					return Phone;
				case 'currency':
					return Deprecation;
				case 'number':
					return Number;
				default:
					return DotsHorizontal;
			}
		}

		return DotsHorizontal;
	}, []);

	const getValue = useCallback(
		(field: InlineField) => {
			if (field.id?.startsWith('fields.')) {
				switch (field.kind) {
					case 'customer': {
						const customerValue = field.value as { name?: string } | undefined;
						return customerValue?.name ?? customerValue?.toString() ?? '';
					}
				}

				if (!field.value) return '';

				switch (field.type) {
					case 'date':
						return DateFormatter.shortDate(DateTime.fromISOString(field.value as string));
					case 'currency':
						return NumberFormatter.money(parseFloat(field.value as string) * 100, 2, currency);
					case 'multiple':
						return (field.value as string[]).join(', ');
					case 'url':
					case 'phone':
					case 'email':
					case 'number':
					default:
						return field.value?.toString() ?? '';
				}
			}

			return field.value?.toString() ?? '';
		},
		[currency]
	);

	// Format fields: add icons and format values for non-location/sublocation fields
	const processedFields = useMemo(() => {
		return fields.map((field) => {
			// For location/sublocation, keep raw value (renderField will handle it)
			if (field.id === SystemFieldType.location || field.id === VirtualFieldType.sublocation) {
				return {
					...field,
					icon: field.icon || getIcon(field),
				};
			}

			// For other fields, format value and set icon
			return {
				...field,
				icon: field.icon || getIcon(field),
				value: getValue(field),
			};
		});
	}, [fields, getIcon, getValue]);

	if (isLoading) {
		// Use default skeleton count when loading, regardless of fields.length
		// This ensures visual feedback even when empty arrays are passed during loading
		const skeletonCount = fields.length > 0 ? fields.length : 3;
		return (
			<Stack spacing="tight" align="center" wrap={wrap} tw="overflow-hidden whitespace-nowrap">
				<Info.Skeleton count={skeletonCount} />
			</Stack>
		);
	}

	return (
		<Stack
			spacing="tight"
			align="center"
			wrap={wrap}
			tw="overflow-hidden whitespace-nowrap [&>*]:flex-none [&>*:last-child]:flex-1"
			{...rest}
		>
			{customInlineFields}
			{processedFields.map((field) => {
				if (!field.value || (Array.isArray(field.value) && field.value.length === 0)) {
					return null;
				}

				// Convert InlineField to FieldData format for renderField
				const fieldData: FieldData = {
					id: field.id,
					value: field.value,
					icon: field.icon,
				};

				// Use renderField for all fields (handles location/sublocation specially)
				return renderField(fieldData);
			})}
		</Stack>
	);
};

export default InlineFields;
export type { InlineField } from '../../../../types';
